import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';

import { Errors } from '../components/error/alertMessages';
import { useCustomSelectors } from '../services/selectors/allSelectors';
import * as images from '../assets/images/index';
import * as Hooks from '../hooks';
import * as Icons from '../assets/svg/icons';
import {
	checkPharmacyLoginCredentials,
	clearTwoFactorLoginError,
	confirmPharmacyLoginAuthenticator,
	sendPharmacyLoginEmailOtp,
	sendPharmacyLoginPhoneOtp,
	setupPharmacyLoginAuthenticator,
	verifyPharmacyLoginAuthenticator,
	verifyPharmacyLoginEmailOtp,
	verifyPharmacyLoginPhoneOtp,
	verifyPharmacyLoginIpAuthorizationOtp
} from '../services/actions/auth/twoFactorAuthActions';
import { useOtpLockout } from './useOtpLockout';

/** The screens that ask for a code, and so the only ones an OTP lockout message belongs on. */
const CODE_ENTRY_STAGES = ['email', 'phone', 'authenticator', 'setup', 'ip_authorization'];

const SAVED_LOGIN_STORAGE_KEYS = [
	'UEgNtTli3A_px-wail',
	'PAgNtTli3A_ps-wrd',
	'checkbox',
	'email',
	'password',
	'lsRememberMe'
];

const clearSavedLoginData = () => {
	SAVED_LOGIN_STORAGE_KEYS.forEach((key) => {
		localStorage.removeItem(key);
		sessionStorage.removeItem(key);
		document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
	});
};

const initialTwoFactorState = {
	challengeToken: '',
	methods: {},
	maskedEmail: '',
	phoneOtpNotice: '',
	phoneOtpNoticeTitle: '',
	phoneOtpNoticeTitleLines: [],
	phoneOtpNoticeDescription: '',
	phoneOtpNoticeDescriptionLines: [],
	emailOtpConfigured: false,
	maskedEmailOtpAddress: '',
	preferredMethod: '',
	setup: null
};

const codeValue = (value) => value.replace(/\D/g, '').slice(0, 6);

const copyTextToClipboard = async (text) => {
	if (navigator?.clipboard?.writeText) {
		await navigator.clipboard.writeText(text);
		return;
	}

	const textArea = document.createElement('textarea');
	textArea.value = text;
	textArea.setAttribute('readonly', '');
	textArea.style.position = 'fixed';
	textArea.style.opacity = '0';
	document.body.appendChild(textArea);
	textArea.select();
	document.execCommand('copy');
	document.body.removeChild(textArea);
};

const MailMethodIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.75" className="w-6 h-6 shrink-0" aria-hidden="true">
		<path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
	</svg>
);

const AuthenticatorMethodIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.75" className="w-6 h-6 shrink-0" aria-hidden="true">
		<path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
		<path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75h4.5M9.75 12.75h4.5" />
	</svg>
);

const PhoneMethodIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="1.75" className="w-6 h-6 shrink-0" aria-hidden="true">
		<path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
	</svg>
);

const methodIconWrapClass = 'flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center border';

const ChevronRightIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors">
		<path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
	</svg>
);

const ArrowLeftIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
		<path fillRule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clipRule="evenodd" />
	</svg>
);

const CheckCircleIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-emerald-500 shrink-0">
		<path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
	</svg>
);

const methodCardClass =
	'group w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-blue-400 hover:shadow-lg hover:shadow-blue-100/60 hover:-translate-y-0.5 transition-all duration-200 text-left disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none';

const inputFieldClass =
	'pl-12 h-12 rounded-xl border border-gray-200 text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 transition-colors duration-200';

const verificationInputFieldClass =
	'pl-12 h-12 rounded-xl border border-gray-200 text-gray-900 text-lg font-bold focus:ring-2 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 transition-colors duration-200';

const codeInputClass = `${verificationInputFieldClass} tracking-widest`;

const inputFieldIconClass =
	'absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors pointer-events-none';

const submitButtonClass =
	'h-12 mt-2 text-white bg-blue-600 hover:bg-blue-700 rounded-xl font-medium text-sm w-full px-5 py-2.5 text-center focus:ring-4 focus:outline-none focus:ring-blue-300 disabled:opacity-70 disabled:cursor-not-allowed transition-colors duration-200';

const helperTextClass = 'text-sm text-gray-600';
const methodTitleClass = 'block text-base font-semibold text-gray-900';
const methodDescriptionClass = 'block text-xs text-gray-500 mt-0.5';
const actionButtonClass = 'text-sm text-blue-600 hover:underline disabled:opacity-70';

const getAvailableVerificationMethods = (methods = {}) => {
	const availableMethods = [];

	if (methods.email_otp) {
		availableMethods.push('email_otp');
	}

	if (methods.phone_otp) {
		availableMethods.push('phone_otp');
	}

	if (methods.authenticator_app || methods.authenticator_setup) {
		availableMethods.push('authenticator');
	}

	return availableMethods;
};

const EmailFieldIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className={inputFieldIconClass} aria-hidden="true">
		<path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
	</svg>
);

const LockFieldIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className={inputFieldIconClass} aria-hidden="true">
		<path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 0 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
	</svg>
);

const OtpFieldIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className={inputFieldIconClass} aria-hidden="true">
		<path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
	</svg>
);

const TwoFactorSignIn = () => {
	const navigate = Hooks.useCustomNavigate();
	const dispatch = Hooks.useCustomDispatch();
	const { authData } = useCustomSelectors();
	const authResponse = authData;

	const [stage, setStage] = useState('credentials');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [emailOtpAddress, setEmailOtpAddress] = useState('');
	const [emailOtp, setEmailOtp] = useState('');
	const [phoneOtp, setPhoneOtp] = useState('');
	const [phoneOtpLabel, setPhoneOtpLabel] = useState('');
	const [authenticatorCode, setAuthenticatorCode] = useState('');
	const [setupCode, setSetupCode] = useState('');
	const [ipAuthorizationOtp, setIpAuthorizationOtp] = useState('');
	const [twoFactor, setTwoFactor] = useState(initialTwoFactorState);
	const [message, setMessage] = useState('');
	const [notice, setNotice] = useState('');
	const [secretCopied, setSecretCopied] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const hasMultipleVerificationMethods = getAvailableVerificationMethods(twoFactor.methods).length > 1;
	// Wrong codes lock code entry for a while, for longer each time. The backend decides;
	// this keeps the form in step and counts the wait down on screen.
	// Keyed by the email being signed in: the backend counts wrong codes per user, so one
	// locked-out account must not hold up the next person to sign in on this browser.
	const { isOtpLocked, otpLockoutMessage, applyLockoutFromError, clearLockout } = useOtpLockout(email);
	// Every code field and Verify button waits out a lockout, as does resending a code.
	const codeEntryDisabled = authResponse?.loading || isOtpLocked;
	// The countdown belongs to code entry. A refresh drops the user back on the password
	// form, where an OTP lockout message would make no sense, so it is only shown on the
	// screens that ask for a code. The backend keeps enforcing the wait either way.
	const isCodeEntryStage = CODE_ENTRY_STAGES.includes(stage);

	// True while the red line on screen is the countdown rather than a message of its own,
	// so the wait can be wiped the moment it ends instead of freezing at "1 second".
	const showingLockoutMessage = useRef(false);

	useEffect(() => {
		if (!isCodeEntryStage) return;

		if (otpLockoutMessage) {
			setMessage(otpLockoutMessage);
			// Drop any "code sent" line: during a lockout that code cannot be used, and leaving
			// it on screen next to the countdown reads as a contradiction.
			setNotice('');
			showingLockoutMessage.current = true;
			return;
		}

		if (showingLockoutMessage.current) {
			setMessage('');
			showingLockoutMessage.current = false;
		}
	}, [isCodeEntryStage, otpLockoutMessage]);

	/**
	 * Shows the lockout countdown for a blocked attempt, or the backend's own message.
	 * The countdown text is left to the effect above, so the wait ticks instead of sitting
	 * on the fixed wording the backend sent ("try again in 5 minutes").
	 */
	const reportVerificationError = (result) => {
		if (applyLockoutFromError(result?.error)) {
			setMessage('');
			setNotice('');
			return;
		}

		setMessage(result.message);
	};

	/**
	 * A send refused by a lockout arrives while the user is still on the password form,
	 * where a code countdown does not belong. Move on to the code screen instead: the
	 * password was accepted, and the wait then ticks down where the user expects it.
	 */
	const reportSendError = (result, codeStage) => {
		if (applyLockoutFromError(result?.error)) {
			setNotice('');
			setMessage('');
			setStage(codeStage);
			return;
		}

		setMessage(result.message);
	};

	useEffect(() => {
		dispatch(clearTwoFactorLoginError());
		clearSavedLoginData();
	}, [dispatch]);

	const resetVerificationState = () => {
		setTwoFactor(initialTwoFactorState);
		setEmailOtpAddress('');
		setEmailOtp('');
		setPhoneOtp('');
		setPhoneOtpLabel('');
		setAuthenticatorCode('');
		setSetupCode('');
		setIpAuthorizationOtp('');
		setSecretCopied(false);
		setNotice('');
	};

	const handleIpAuthorizationRequired = (result) => {
		const data = result?.data || {};

		if (!data.ip_authorization_required) {
			return false;
		}

		setTwoFactor((current) => ({
			...current,
			challengeToken: data.challenge_token || current.challengeToken
		}));
		setIpAuthorizationOtp('');
		setNotice(data.message || result.message || 'Authorization code sent to the pharmacy authorization numbers.');
		setStage('ip_authorization');
		return true;
	};

	const sendEmailOtpForChallenge = async (challengeToken, targetEmail = null) => {
		setMessage('');
		setNotice('');

		// Always ask the backend, even when this browser thinks the user is locked out: it is
		// the one that knows, it refuses to send while a lockout stands (so no code reaches
		// the account holder), and its answer re-syncs the wait shown here. A lockout cleared
		// by support therefore releases the form instead of stranding it behind a stale timer.
		const result = await dispatch(sendPharmacyLoginEmailOtp(challengeToken, targetEmail));

		if (result?.error) {
			reportSendError(result, 'email');
			return false;
		}

		// The backend sent a code, so any wait this browser was showing is over.
		clearLockout();

		setTwoFactor((current) => ({
			...current,
			maskedEmail: result?.data?.masked_email || current.maskedEmail,
			emailOtpConfigured: result?.data?.email_otp_configured ?? current.emailOtpConfigured,
			maskedEmailOtpAddress: result?.data?.masked_email || current.maskedEmailOtpAddress
		}));
		setNotice('Verification code sent to your email.');
		setStage('email');
		return true;
	};

	const handleSendEmailOtp = async () => {
		if (twoFactor.emailOtpConfigured) {
			await sendEmailOtpForChallenge(twoFactor.challengeToken);
			return;
		}

		setEmailOtp('');
		setEmailOtpAddress('');
		setMessage('');
		setNotice('');
		setStage('email_setup');
	};

	const handleGenerateEmailOtp = async (event) => {
		event.preventDefault();
		const targetEmail = emailOtpAddress.trim();

		if (!targetEmail) {
			setMessage('Email is required. Please enter the email address for OTP.');
			return;
		}

		await sendEmailOtpForChallenge(twoFactor.challengeToken, targetEmail);
	};

	const handleVerifyEmailOtp = async (event) => {
		event.preventDefault();
		setMessage('');

		if (isOtpLocked) {
			setMessage(otpLockoutMessage);
			return;
		}

		if (emailOtp.length !== 6) {
			setMessage('Please enter the 6-digit email code.');
			return;
		}

		const result = await dispatch(verifyPharmacyLoginEmailOtp(twoFactor.challengeToken, emailOtp, navigate));

		if (result?.error) {
			reportVerificationError(result);
			return;
		}

		clearLockout();
		handleIpAuthorizationRequired(result);
	};

	const sendPhoneOtpForChallenge = async (challengeToken, methods = twoFactor.methods) => {
		setMessage('');
		setNotice('');

		if (!methods?.phone_otp) {
			setMessage('SMS OTP is no longer available. Please set up an Authenticator App.');
			return false;
		}

		// Always ask the backend: it refuses to send while a lockout stands, so no SMS reaches
		// the account holder, and its answer re-syncs the wait shown here.
		const result = await dispatch(sendPharmacyLoginPhoneOtp(challengeToken));

		if (result?.error) {
			reportSendError(result, 'phone');
			return false;
		}

		// The backend sent a code, so any wait this browser was showing is over.
		clearLockout();

		const data = result?.data || {};
		const label = data.delivery_target === 'user_phone' && data.masked_phone
			? data.masked_phone
			: 'pharmacy authorization numbers';

		setPhoneOtp('');
		setPhoneOtpLabel(label);
		setNotice(data.message || result.message || 'Verification code sent.');
		setStage('phone');
		return true;
	};

	const handleSendPhoneOtp = async () => {
		await sendPhoneOtpForChallenge(twoFactor.challengeToken);
	};

	const handleVerifyPhoneOtp = async (event) => {
		event.preventDefault();
		setMessage('');

		if (isOtpLocked) {
			setMessage(otpLockoutMessage);
			return;
		}

		if (phoneOtp.length !== 6) {
			setMessage('Please enter the 6-digit SMS code.');
			return;
		}

		const result = await dispatch(verifyPharmacyLoginPhoneOtp(twoFactor.challengeToken, phoneOtp, navigate));

		if (result?.error) {
			reportVerificationError(result);
			return;
		}

		clearLockout();
		handleIpAuthorizationRequired(result);
	};

	const startAuthenticatorSetupForChallenge = async (challengeToken) => {
		setMessage('');
		setNotice('');
		setSecretCopied(false);

		const result = await dispatch(setupPharmacyLoginAuthenticator(challengeToken));

		if (result?.error) {
			setMessage(result.message);
			return false;
		}

		setTwoFactor((current) => ({
			...current,
			setup: result?.data || null
		}));
		setNotice('Authenticator setup started.');
		setStage('setup');
		return true;
	};

	const handleCopySecret = async () => {
		if (!twoFactor.setup?.secret) {
			return;
		}

		try {
			await copyTextToClipboard(twoFactor.setup.secret);
			setSecretCopied(true);
			setTimeout(() => setSecretCopied(false), 1800);
		} catch (error) {
			setMessage('Unable to copy the secret key.');
		}
	};

	const handleAuthenticatorChoice = async () => {
		setMessage('');
		setNotice('');

		if (twoFactor.methods?.authenticator_app) {
			setStage('authenticator');
			return;
		}

		await handleStartAuthenticatorSetup();
	};

	const handleStartAuthenticatorSetup = async () => {
		await startAuthenticatorSetupForChallenge(twoFactor.challengeToken);
	};

	const routeSingleVerificationMethod = async (data) => {
		const methods = data.methods || {};
		const preferredMethod = data.preferred_two_factor_method || '';

		if (preferredMethod === 'email_otp' && methods.email_otp && data.email_otp_configured) {
			await sendEmailOtpForChallenge(data.challenge_token);
			return true;
		}

		if (preferredMethod === 'authenticator_app' && methods.authenticator_app) {
			setStage('authenticator');
			return true;
		}

		const availableMethods = getAvailableVerificationMethods(methods);

		if (availableMethods.length !== 1) {
			return false;
		}

		const [method] = availableMethods;

		if (method === 'email_otp') {
			if (data.email_otp_configured) {
				await sendEmailOtpForChallenge(data.challenge_token);
				return true;
			}

			setStage('email_setup');
			return true;
		}

		if (method === 'phone_otp') {
			await sendPhoneOtpForChallenge(data.challenge_token, methods);
			return true;
		}

		if (methods.authenticator_app) {
			setStage('authenticator');
			return true;
		}

		await startAuthenticatorSetupForChallenge(data.challenge_token);
		return true;
	};

	const handleCredentialsSubmit = async (event) => {
		event.preventDefault();
		setMessage('');
		setNotice('');

		if (!email) {
			setMessage('Email is required. Please enter your email address.');
			return;
		}

		if (!password) {
			setMessage('Password is required. Please enter a valid password.');
			return;
		}

		clearSavedLoginData();
		const result = await dispatch(checkPharmacyLoginCredentials(email, password));

		if (result?.error) {
			setMessage(result.message);
			return;
		}

		const data = result?.data || {};
		setTwoFactor({
			challengeToken: data.challenge_token,
			methods: data.methods || {},
			maskedEmail: data.masked_email || '',
			phoneOtpNotice: data.phone_otp_notice || '',
			phoneOtpNoticeTitle: data.phone_otp_notice_title || '',
			phoneOtpNoticeTitleLines: data.phone_otp_notice_title_lines || [],
			phoneOtpNoticeDescription: data.phone_otp_notice_description || '',
			phoneOtpNoticeDescriptionLines: data.phone_otp_notice_description_lines || [],
			emailOtpConfigured: data.email_otp_configured || false,
			maskedEmailOtpAddress: data.masked_email_otp_address || '',
			preferredMethod: data.preferred_two_factor_method || '',
			setup: null
		});

		if (await routeSingleVerificationMethod(data)) {
			return;
		}

		setStage('method');
	};

	const handleVerifyAuthenticator = async (event) => {
		event.preventDefault();
		setMessage('');

		if (isOtpLocked) {
			setMessage(otpLockoutMessage);
			return;
		}

		if (authenticatorCode.length !== 6) {
			setMessage('Please enter the 6-digit authenticator code.');
			return;
		}

		const result = await dispatch(verifyPharmacyLoginAuthenticator(twoFactor.challengeToken, authenticatorCode, navigate));

		if (result?.error) {
			reportVerificationError(result);
			return;
		}

		clearLockout();
		handleIpAuthorizationRequired(result);
	};

	const handleConfirmAuthenticatorSetup = async (event) => {
		event.preventDefault();
		setMessage('');

		if (isOtpLocked) {
			setMessage(otpLockoutMessage);
			return;
		}

		if (setupCode.length !== 6) {
			setMessage('Please enter the 6-digit authenticator code.');
			return;
		}

		const result = await dispatch(confirmPharmacyLoginAuthenticator(twoFactor.challengeToken, setupCode, navigate));

		if (result?.error) {
			reportVerificationError(result);
			return;
		}

		clearLockout();
		handleIpAuthorizationRequired(result);
	};

	const handleVerifyIpAuthorizationOtp = async (event) => {
		event.preventDefault();
		setMessage('');

		if (isOtpLocked) {
			setMessage(otpLockoutMessage);
			return;
		}

		if (ipAuthorizationOtp.length !== 6) {
			setMessage('Please enter the 6-digit authorization code.');
			return;
		}

		const result = await dispatch(verifyPharmacyLoginIpAuthorizationOtp(twoFactor.challengeToken, ipAuthorizationOtp, navigate));

		if (result?.error) {
			reportVerificationError(result);
			return;
		}

		clearLockout();
	};

	const handleBackToPassword = () => {
		resetVerificationState();
		setStage('credentials');
		setMessage('');
	};

	const renderCredentials = () => (
		<form onSubmit={handleCredentialsSubmit}>
			<p className="text-sm text-gray-500 text-center mb-5 leading-relaxed">
				Sign in to continue.
			</p>

			<div className="mb-3">
				<label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
				<div className="relative group">
					<EmailFieldIcon />
					<input
						required
						type="email"
						id="email"
						value={email}
						name="email"
						autoComplete="off"
						onChange={(event) => {
							setEmail(event.target.value);
							setMessage('');
							resetVerificationState();
						}}
						className={inputFieldClass}
						placeholder="Enter email"
					/>
				</div>
			</div>

			<div className="mb-3">
				<label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
				<div className="relative group">
					<LockFieldIcon />
					<input
						required
						value={password}
						type={showPassword ? 'text' : 'password'}
						onChange={(event) => {
							setPassword(event.target.value);
							setMessage('');
							resetVerificationState();
						}}
						name="password"
						id="password"
						className={`${inputFieldClass} pr-12`}
						placeholder="Enter password"
					/>
					<button
						type="button"
						onClick={() => setShowPassword(!showPassword)}
						className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 focus:outline-none shrink-0"
						aria-label={showPassword ? 'Hide password' : 'Show password'}
					>
						<span className="relative block w-5 h-5 flex items-center justify-center">
							{!showPassword ? (
								<Icons.CloseEyeIcon className="w-5 h-5 shrink-0 block" width={20} height={20} />
							) : (
								<Icons.EyeIcon className="w-5 h-5 shrink-0 block" size={20} />
							)}
						</span>
					</button>
				</div>
			</div>

			{message && <Errors error={message} />}

			<button
				type="submit"
				disabled={authResponse?.loading}
				className={submitButtonClass}
			>
				{authResponse?.loading ? 'Processing...' : 'Login'}
			</button>
		</form>
	);

	const renderPhoneOtpNotice = () => {
		const title = twoFactor.phoneOtpNoticeTitle;
		const description = twoFactor.phoneOtpNoticeDescription || (!title ? twoFactor.phoneOtpNotice : '');
		const titleLines = twoFactor.phoneOtpNoticeTitleLines?.length ? twoFactor.phoneOtpNoticeTitleLines : (title ? [title] : []);
		const descriptionLines = twoFactor.phoneOtpNoticeDescriptionLines?.length
			? twoFactor.phoneOtpNoticeDescriptionLines
			: (description ? [description] : []);

		if (!titleLines.length && !descriptionLines.length) {
			return null;
		}

		return (
			<div className="mb-3 rounded-xl border border-[#3B82F6] bg-gradient-to-r from-[#2563EB] to-[#1E3A8A] px-4 py-4 text-center shadow-sm">
				{titleLines.length > 0 && (
					<p className="text-sm font-semibold leading-tight text-[#EFF6FF]">
						{titleLines.map((line) => (
							<span key={line} className="block">{line}</span>
						))}
					</p>
				)}
				{descriptionLines.length > 0 && (
					<p className={`${titleLines.length ? 'mt-2' : ''} text-[10px] font-semibold leading-tight text-[#EFF6FF]`}>
						{descriptionLines.map((line) => (
							<span key={line} className="block whitespace-nowrap">{line}</span>
						))}
					</p>
				)}
			</div>
		);
	};

	const renderMethodChoice = () => (
		<div>
			<p className="text-sm text-gray-500 text-center mb-5 leading-relaxed">
				Choose a verification method to continue.
			</p>
			{twoFactor.methods?.phone_otp && renderPhoneOtpNotice()}
			<div className="space-y-3">
				{twoFactor.methods?.email_otp && (
					<button
						type="button"
						onClick={handleSendEmailOtp}
						disabled={authResponse?.loading}
						className={methodCardClass}
					>
						<span className={methodIconWrapClass} style={{ backgroundColor: '#eff6ff', borderColor: '#dbeafe' }}>
							<MailMethodIcon />
						</span>
						<span className="flex-1 min-w-0">
							<span className={methodTitleClass}>Email OTP</span>
							<span className={methodDescriptionClass}>
								{twoFactor.emailOtpConfigured && twoFactor.maskedEmailOtpAddress
									? `Send a one-time code to ${twoFactor.maskedEmailOtpAddress}.`
									: 'Set up email OTP and receive a one-time code.'}
							</span>
						</span>
						<ChevronRightIcon />
					</button>
				)}

				{twoFactor.methods?.phone_otp && (
					<button
						type="button"
						onClick={handleSendPhoneOtp}
						disabled={authResponse?.loading}
						className={methodCardClass}
					>
						<span className={methodIconWrapClass} style={{ backgroundColor: '#ecfdf5', borderColor: '#d1fae5' }}>
							<PhoneMethodIcon />
						</span>
						<span className="flex-1 min-w-0">
							<span className={methodTitleClass}>SMS OTP</span>
							<span className={methodDescriptionClass}>
								Send a one-time code to your registered mobile number.
							</span>
						</span>
						<ChevronRightIcon />
					</button>
				)}

				<button
					type="button"
					onClick={handleAuthenticatorChoice}
					disabled={authResponse?.loading}
					className={methodCardClass}
				>
					<span className={methodIconWrapClass} style={{ backgroundColor: '#eef2ff', borderColor: '#e0e7ff' }}>
						<AuthenticatorMethodIcon />
					</span>
					<span className="flex-1 min-w-0">
						<span className={methodTitleClass}>Authenticator App</span>
						<span className={methodDescriptionClass}>
							Enter the code from your authenticator app.
						</span>
					</span>
					<ChevronRightIcon />
				</button>
			</div>

			{message && <div className="mt-4"><Errors error={message} /></div>}

			<button
				type="button"
				onClick={handleBackToPassword}
				className="mt-6 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors"
			>
				<ArrowLeftIcon />
				Back to password
			</button>
		</div>
	);

	const renderEmailOtpSetup = () => (
		<form onSubmit={handleGenerateEmailOtp}>
			<p className={`${helperTextClass} text-center mb-4`}>
				Enter the email address where you want to receive login codes.
			</p>

			<div className="mb-3">
				<label htmlFor="emailOtpAddress" className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
				<div className="relative group">
					<EmailFieldIcon />
					<input
						required
						type="email"
						id="emailOtpAddress"
						value={emailOtpAddress}
						name="emailOtpAddress"
						autoComplete="off"
						onChange={(event) => {
							setEmailOtpAddress(event.target.value);
							setMessage('');
						}}
						className={verificationInputFieldClass}
						placeholder="Enter email"
					/>
				</div>
			</div>

			{message && <Errors error={message} />}

			<button type="submit" disabled={authResponse?.loading} className={submitButtonClass}>
				{authResponse?.loading ? 'Sending...' : 'Generate OTP'}
			</button>

			{hasMultipleVerificationMethods && (
				<button type="button" onClick={() => setStage('method')} className={`mt-4 ${actionButtonClass}`}>
					Change method
				</button>
			)}
		</form>
	);

	const renderEmailOtp = () => (
		<form onSubmit={handleVerifyEmailOtp}>
			<p className={`${helperTextClass} text-center mb-4`}>
				Enter the code sent to {twoFactor.maskedEmailOtpAddress || twoFactor.maskedEmail || 'your email'}.
			</p>

			<div className="mb-3">
				<label htmlFor="emailOtp" className="block text-sm font-medium text-gray-700 mb-1.5">Email OTP</label>
				<div className="relative group">
					<OtpFieldIcon />
					<input
						required
						value={emailOtp}
						disabled={isOtpLocked}
						inputMode="numeric"
						onChange={(event) => setEmailOtp(codeValue(event.target.value))}
						name="emailOtp"
						id="emailOtp"
						className={codeInputClass}
						placeholder="Enter 6-digit code"
					/>
				</div>
			</div>

			{notice && <p className="text-sm text-green-700 bg-green-50 rounded-lg p-3 mb-3 text-center">{notice}</p>}
			{message && <Errors error={message} />}

			<button type="submit" disabled={codeEntryDisabled} className={submitButtonClass}>
				{authResponse?.loading ? 'Verifying...' : 'Verify and Continue'}
			</button>

			<div className={`flex mt-4 ${hasMultipleVerificationMethods ? 'justify-between' : 'justify-end'}`}>
				{hasMultipleVerificationMethods && (
					<button type="button" onClick={() => setStage('method')} className={actionButtonClass}>Change method</button>
				)}
				<button
					type="button"
					onClick={() => sendEmailOtpForChallenge(twoFactor.challengeToken, twoFactor.emailOtpConfigured ? null : emailOtpAddress.trim())}
					disabled={authResponse?.loading}
					className={actionButtonClass}
				>
					Resend code
				</button>
			</div>
		</form>
	);

	const renderPhoneOtp = () => (
		<form onSubmit={handleVerifyPhoneOtp}>
			{renderPhoneOtpNotice()}
			<p className={`${helperTextClass} text-center mb-4`}>
				Enter the code sent to {phoneOtpLabel || 'your registered mobile number'}.
			</p>

			<div className="mb-3">
				<label htmlFor="phoneOtp" className="block text-sm font-medium text-gray-700 mb-1.5">SMS OTP</label>
				<div className="relative group">
					<OtpFieldIcon />
					<input
						required
						value={phoneOtp}
						disabled={isOtpLocked}
						inputMode="numeric"
						onChange={(event) => setPhoneOtp(codeValue(event.target.value))}
						name="phoneOtp"
						id="phoneOtp"
						className={codeInputClass}
						placeholder="Enter 6-digit code"
					/>
				</div>
			</div>

			{notice && <p className="text-sm text-green-700 bg-green-50 rounded-lg p-3 mb-3 text-center">{notice}</p>}
			{message && <Errors error={message} />}

			<button type="submit" disabled={codeEntryDisabled} className={submitButtonClass}>
				{authResponse?.loading ? 'Verifying...' : 'Verify and Continue'}
			</button>

			<div className={`flex mt-4 ${hasMultipleVerificationMethods ? 'justify-between' : 'justify-end'}`}>
				{hasMultipleVerificationMethods && (
					<button type="button" onClick={() => setStage('method')} className={actionButtonClass}>Change method</button>
				)}
				<button type="button" onClick={handleSendPhoneOtp} disabled={authResponse?.loading} className={actionButtonClass}>Resend code</button>
			</div>
		</form>
	);

	const renderAuthenticator = () => (
		<form onSubmit={handleVerifyAuthenticator}>
			<p className={`${helperTextClass} text-center mb-4`}>Enter the current code from your authenticator app.</p>

			<div className="mb-3">
				<label htmlFor="authenticatorCode" className="block text-sm font-medium text-gray-700 mb-1.5">Authenticator Code</label>
				<div className="relative group">
					<OtpFieldIcon />
					<input
						required
						value={authenticatorCode}
						disabled={isOtpLocked}
						inputMode="numeric"
						onChange={(event) => setAuthenticatorCode(codeValue(event.target.value))}
						name="authenticatorCode"
						id="authenticatorCode"
						className={codeInputClass}
						placeholder="Enter 6-digit code"
					/>
				</div>
			</div>

			{message && <Errors error={message} />}

			<button type="submit" disabled={codeEntryDisabled} className={submitButtonClass}>
				{authResponse?.loading ? 'Verifying...' : 'Verify and Continue'}
			</button>

			{hasMultipleVerificationMethods && (
				<button type="button" onClick={() => setStage('method')} className={`mt-4 ${actionButtonClass}`}>
					Change method
				</button>
			)}
		</form>
	);

	const renderIpAuthorization = () => (
		<form onSubmit={handleVerifyIpAuthorizationOtp}>
			<p className={`${helperTextClass} text-center mb-4`}>
				Enter the authorization code sent to the pharmacy authorization numbers.
			</p>

			<div className="mb-3">
				<label htmlFor="ipAuthorizationOtp" className="block text-sm font-medium text-gray-700 mb-1.5">Authorization Code</label>
				<div className="relative group">
					<OtpFieldIcon />
					<input
						required
						value={ipAuthorizationOtp}
						disabled={isOtpLocked}
						inputMode="numeric"
						onChange={(event) => setIpAuthorizationOtp(codeValue(event.target.value))}
						name="ipAuthorizationOtp"
						id="ipAuthorizationOtp"
						className={codeInputClass}
						placeholder="Enter 6-digit code"
					/>
				</div>
			</div>

			{notice && <p className="text-sm text-green-700 bg-green-50 rounded-lg p-3 mb-3 text-center">{notice}</p>}
			{message && <Errors error={message} />}

			<button type="submit" disabled={codeEntryDisabled} className={submitButtonClass}>
				{authResponse?.loading ? 'Verifying...' : 'Verify and Continue'}
			</button>

			{hasMultipleVerificationMethods && (
				<button type="button" onClick={() => setStage('method')} className={`mt-4 ${actionButtonClass}`}>
					Change method
				</button>
			)}
		</form>
	);

	const renderSetup = () => (
		<form onSubmit={handleConfirmAuthenticatorSetup}>
			<p className="text-sm text-gray-500 text-center mb-4 leading-relaxed">
				Scan the QR code with your authenticator app, then enter the current code.
			</p>

			<div className="flex items-center justify-center gap-2 mb-4">
				<div className="flex items-center gap-2">
					<span className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shadow-sm">1</span>
					<span className="text-xs font-medium text-gray-700">Scan QR</span>
				</div>
				<div className="w-8 border-t border-dashed border-gray-300" />
				<div className="flex items-center gap-2">
					<span className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 text-xs font-bold flex items-center justify-center">2</span>
					<span className="text-xs font-medium text-gray-400">Enter code</span>
				</div>
			</div>

			<div className="mb-3 rounded-2xl border border-gray-100 bg-gradient-to-b from-slate-50 to-white p-4">
				{twoFactor.setup?.qr_code ? (
					<>
						<div className="flex justify-center">
							<div className="p-4 bg-white rounded-2xl shadow-lg shadow-gray-200/70 ring-1 ring-gray-100">
								<img
									src={twoFactor.setup.qr_code}
									alt="Authenticator app setup QR code"
									className="w-40 h-40"
								/>
							</div>
						</div>
						<p className="text-center text-xs text-gray-400 mt-3">
							Compatible with Google Authenticator, Authy, and similar apps
						</p>
					</>
				) : (
					<p className="text-sm text-red-600 text-center py-4">QR code could not be loaded. Please change method and try again.</p>
				)}
			</div>

			{twoFactor.setup?.secret && (
				<div className="mb-3 flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50/70 px-3 py-2">
					<div className="min-w-0 flex-1">
						<p className="text-xs font-semibold text-blue-700 mb-0.5">Secret key</p>
						<p className="font-mono text-xs leading-snug text-gray-800 break-all">{twoFactor.setup.secret}</p>
					</div>
					<button
						type="button"
						onClick={handleCopySecret}
						className="h-8 w-8 shrink-0 rounded-lg border border-blue-100 bg-white text-blue-600 flex items-center justify-center transition-colors hover:bg-blue-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
						aria-label={secretCopied ? 'Secret key copied' : 'Copy secret key'}
						title={secretCopied ? 'Copied' : 'Copy secret key'}
					>
						{secretCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
					</button>
				</div>
			)}

			<div className="mb-3">
				<label htmlFor="setupCode" className="block text-sm font-medium text-gray-700 mb-1.5">Authenticator Code</label>
				<div className="relative group">
					<OtpFieldIcon />
					<input
						required
						value={setupCode}
						disabled={isOtpLocked}
						inputMode="numeric"
						onChange={(event) => setSetupCode(codeValue(event.target.value))}
						name="setupCode"
						id="setupCode"
						className={codeInputClass}
						placeholder="Enter 6-digit code"
					/>
				</div>
			</div>

			{notice && (
				<div className="flex items-center gap-2.5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 mb-4">
					<CheckCircleIcon />
					<span>{notice}</span>
				</div>
			)}
			{message && <Errors error={message} />}

			<button type="submit" disabled={codeEntryDisabled} className={submitButtonClass}>
				{authResponse?.loading ? 'Confirming...' : 'Confirm and Continue'}
			</button>

			{hasMultipleVerificationMethods && (
				<button type="button" onClick={() => setStage('method')} className={`mt-4 inline-flex items-center gap-1.5 ${actionButtonClass}`}>
					<ArrowLeftIcon />
					Change method
				</button>
			)}
		</form>
	);

	const renderStage = () => {
		if (stage === 'method') return renderMethodChoice();
		if (stage === 'email_setup') return renderEmailOtpSetup();
		if (stage === 'email') return renderEmailOtp();
		if (stage === 'phone') return renderPhoneOtp();
		if (stage === 'authenticator') return renderAuthenticator();
		if (stage === 'ip_authorization') return renderIpAuthorization();
		if (stage === 'setup') return renderSetup();
		return renderCredentials();
	};

	return (
		<div
			className="min-h-screen flex items-center justify-center px-4"
			style={{ background: 'linear-gradient(90deg, #E9EDF6 0%, #F6F2EA 100%)' }}
		>
			<div className="w-full max-w-md mx-auto">
				<div className="bg-white rounded-2xl shadow border border-gray-100 p-8 formBgShadow">
					<div className="flex justify-center mb-1">
						<img src={images.oatrx_logo} alt="OATRx" className="h-12 w-auto max-w-[180px]" />
					</div>
					<h2 className={`text-center text-gray-900 font-medium text-xl rubik-100 ${stage === 'credentials' ? 'mb-1' : 'mb-3'}`}>Pharmacy Management</h2>
					{renderStage()}
				</div>
				<footer className="mt-6 text-center text-gray-800 text-xs" style={{ color: '#666' }}>
					<p>Secure login for authorized pharmacy personnel only.</p>
					<p className="mt-1">
						Powered by{' '}
						<a
							href="https://medidoze.com"
							target="_blank"
							rel="noopener noreferrer"
							className="font-semibold text-gray-600 hover:text-gray-800 hover:underline"
						>
							Medidoze Technologies Inc.
						</a>
					</p>
				</footer>
			</div>
		</div>
	);
};

export default TwoFactorSignIn;
